/**
 * GlyphCacheManager.ts
 *
 * Manages the global glyph operations cache stored in window.__edg_glyphOps.
 * This cache stores pre-computed glyph outlines and metrics to avoid expensive
 * re-computation during rendering.
 *
 * Extracted from EdgeDocsCanvas.V3.tsx (Step 1 of Phase 3 refactoring)
 * Original location: Lines 724-729, scattered throughout renderText
 *
 * Key: `${fontUid}@${sizeInWorldPx}:${glyphId}`
 * Value: Glyph outline operations (path commands, metrics)
 */

/**
 * Type definition for the global glyph cache
 */
export type GlyphCache = Map<string, any>;

/**
 * Status information about the glyph cache
 */
export interface GlyphCacheStatus {
  exists: boolean;
  size: number;
  sampleKeys: string[];
}

/**
 * Get the global glyph operations cache
 *
 * @returns The glyph cache Map, or undefined if not initialized
 */
export function getGlyphCache(): GlyphCache | undefined {
  return (window as any).__edg_glyphOps;
}

/**
 * Check if a glyph is cached
 *
 * @param cacheKey - The glyph cache key (format: fontUid@size:gid)
 * @returns True if the glyph is cached, false otherwise
 */
export function isGlyphCached(cacheKey: string): boolean {
  const cache = getGlyphCache();
  return cache ? cache.has(cacheKey) : false;
}

/**
 * Get cached glyph operations
 *
 * @param cacheKey - The glyph cache key (format: fontUid@size:gid)
 * @returns The cached glyph operations, or undefined if not cached
 */
export function getCachedGlyph(cacheKey: string): any | undefined {
  const cache = getGlyphCache();
  return cache?.get(cacheKey);
}

/**
 * Get comprehensive cache status for debugging
 *
 * @param sampleCount - Number of sample keys to include (default: 3)
 * @returns Cache status object with existence, size, and sample keys
 */
export function getGlyphCacheStatus(sampleCount: number = 3): GlyphCacheStatus {
  const cache = getGlyphCache();

  return {
    exists: !!cache,
    size: cache?.size || 0,
    sampleKeys: cache ? Array.from(cache.keys()).slice(0, sampleCount) : []
  };
}

/**
 * Build a glyph cache key from components
 *
 * @param fontUid - The font unique identifier
 * @param sizeInWorldPx - The font size in world pixels (DPR-adjusted)
 * @param glyphId - The glyph ID (GID)
 * @returns The formatted cache key
 */
export function buildGlyphCacheKey(
  fontUid: string,
  sizeInWorldPx: number,
  glyphId: number
): string {
  return `${fontUid}@${sizeInWorldPx}:${glyphId}`;
}

/**
 * Quantize font size for cache key consistency
 * Rounds to nearest 1/64th to avoid floating point precision issues
 *
 * @param size - The raw font size
 * @returns Quantized size (rounded to nearest 1/64)
 */
export function quantizeFontSize(size: number): number {
  return Math.round(size * 64) / 64;
}

/**
 * Filter an array of glyph IDs to only those not yet cached
 * Useful for batch glyph loading to avoid redundant work
 *
 * @param glyphIds - Array of glyph IDs to check
 * @param fontUid - The font unique identifier
 * @param fontSizePx - The font size in pixels
 * @returns Array of uncached glyph IDs
 */
export function filterUncachedGlyphs(
  glyphIds: number[],
  fontUid: string,
  fontSizePx: number
): number[] {
  const cache = getGlyphCache();
  if (!cache) {
    // No cache exists, all glyphs are uncached
    return glyphIds;
  }

  const sizeKey = quantizeFontSize(fontSizePx);

  return glyphIds.filter(gid => {
    const cacheKey = buildGlyphCacheKey(fontUid, sizeKey, gid);
    return !cache.has(cacheKey);
  });
}

/**
 * Check if the cache has any entries with world-space coordinates
 * Used for debugging coordinate system issues
 *
 * @returns True if cache exists and has entries
 */
export function hasWorldSpaceCoordinates(): boolean {
  const cache = getGlyphCache();
  return !!(cache && cache.size > 0);
}

/**
 * Get a sample cache entry for debugging
 *
 * @returns Object with sample key and format description, or null if cache empty
 */
export function getSampleCacheEntry(): { key: string; format: string } | null {
  const cache = getGlyphCache();
  if (!cache || cache.size === 0) {
    return null;
  }

  const sampleKey = Array.from(cache.keys())[0] as string;
  return {
    key: sampleKey,
    format: 'font@size:gid (world px size, not CSS px)'
  };
}
